import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { StorageManager } from '../../../storage-manager.class';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { Form, FormType } from '../interfaces/form.interface';
import { FormOperations } from '../utils/form-operations';

@Component({
    selector: 'app-reactive-request-button',
    standalone: false,
    templateUrl: './request-button.component.html',
    styleUrls: ['./request-button.component.scss']
})
export class ReactiveRequestButtonComponent<T> implements OnInit {
    @Input() form!: Form<T>;
    @Input() model!: FormGroup;
    @Input() recordHasAssociatedData = false;
    @Input() request = new RequestHandler<any>();
    @Input() getRequest = new RequestHandler<any>();
    @Input() lockOnSuccess = false;

    @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
    @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();

    public validationError: string | null = null;
    public validationSummary: string | null = null;
    public warning: string | null = null;
    public requestError: string[] = [];
    public readonly REQUEST_BUTTON_TYPES = FormType;

    private hasSubmittedRequest = false;
    private hasCompletedRequest = false;
    private formOperations!: FormOperations<T>;

    /**
     * Returns the user-facing label according to the real submit state.
     */
    public get buttonLabel(): string {
        const [defaultLabel, loadingLabel, doneLabel] = this.buttonLabels[this.currentFormType];

        if (this.warning) {
            return this.warning;
        }

        if (this.hasRequestError) {
            return this.request.hasErrorCliExists === true ? 'Cliente ya existe...' : this.requestError[0] ?? '';
        }

        if (!this.hasSubmittedRequest) {
            return defaultLabel;
        }

        if (this.isRequestLoading) {
            return loadingLabel;
        }

        if (this.hasSuccessfulRequest) {
            return doneLabel;
        }

        return defaultLabel;
    }

    /**
     * Returns CSS classes for the action button.
     */
    public get buttonClasses(): Record<string, boolean> {
        return {
            'btn-danger': Boolean(
                this.validationError ||
                (!this.warning && this.currentFormType === FormType.DELETION && !this.isRequestLoading && !this.hasSubmittedRequest)
            ),
            'btn-warning': Boolean(this.warning || this.hasRequestError || this.hasClientExistsError),
            'btn-primary': Boolean(
                !this.validationError &&
                !this.warning &&
                this.currentFormType !== FormType.DELETION &&
                !this.isRequestLoading &&
                !this.hasSubmittedRequest
            ),
            'btn-success': Boolean(!this.validationError && !this.warning && this.hasSuccessfulRequest),
            'btn-secondary': Boolean(this.hasSubmittedRequest && this.isRequestLoading)
        };
    }

    /**
     * Returns CSS classes for the icon rendered inside the button.
     */
    public get iconClasses(): Record<string, boolean> {
        return {
            'fa-exclamation-triangle': Boolean(this.validationError || this.warning || this.hasRequestError || this.hasClientExistsError),
            'fa-save': Boolean(
                !this.hasSuccessfulRequest &&
                (this.currentFormType === FormType.DUPLICATION ||
                    this.currentFormType === FormType.EDITION ||
                    (this.currentFormType === FormType.CREATION && !this.validationError && !this.warning))
            ),
            'fa-pencil': Boolean(!this.hasSuccessfulRequest && this.currentFormType === FormType.EDITION),
            'fa-trash': Boolean(!this.hasSuccessfulRequest && !this.warning && this.currentFormType === FormType.DELETION),
            'fa-check': Boolean(!this.validationError && !this.warning && this.hasSuccessfulRequest),
            'fa-spinner fa-spin': Boolean(this.hasSubmittedRequest && this.isRequestLoading)
        };
    }

    /**
     * Indicates whether the button must be disabled.
     */
    public get isButtonDisabled(): boolean {
        return this.isRequestLoading || (this.hasSuccessfulRequest && this.lockOnSuccess);
    }

    /**
     * Indicates whether the secondary text for creating or duplicating another record must be shown.
     */
    public get showRepeatActionLabel(): boolean {
        return !this.lockOnSuccess &&
            this.hasSuccessfulRequest &&
            (this.currentFormType === FormType.DUPLICATION || this.currentFormType === FormType.CREATION);
    }

    /**
     * Returns the secondary label for repeated creation or duplication.
     */
    public get repeatActionLabel(): string {
        return this.currentFormType === FormType.DUPLICATION ? 'Duplicar otro' : 'Crear otro';
    }

    private get currentFormType(): FormType {
        return this.form?.type ?? FormType.CREATION;
    }

    private get isRequestLoading(): boolean {
        return this.request.isLoading === true;
    }

    private get hasRequestError(): boolean {
        return this.hasSubmittedRequest && this.request.hasError === true;
    }

    private get hasClientExistsError(): boolean {
        return this.hasSubmittedRequest && this.request.hasErrorCliExists === true;
    }

    private get hasSuccessfulRequest(): boolean {
        return this.hasSubmittedRequest && this.hasCompletedRequest && this.request.hasError === false;
    }

    private get buttonLabels(): Record<FormType, [string, string, string]> {
        return {
            [FormType.CREATION]: ['Guardar y continuar', 'Guardando...', 'Guardado'],
            [FormType.EDITION]: ['Guardar cambios', 'Guardando cambios...', 'Cambios guardados'],
            [FormType.DUPLICATION]: ['Duplicar', 'Duplicando...', 'Duplicado'],
            [FormType.DELETION]: ['Borrar registro', 'Borrando registro...', 'Registro borrado'],
            [FormType.INFORMATIVE]: ['', '', ''],
            [FormType.EXPORT]: ['', '', ''],
            [FormType.GENERATION]: ['Generar informe', 'Generando informe...', 'Informe generado']
        };
    }

    /**
     * Sets generic errors for requests.
     */
    public ngOnInit(): void {
        const isDemo = (StorageManager.getUser() || {}).rol === 'demo';

        this.requestError[0] = isDemo
            ? 'Operación no permitida'
            : 'No hemos podido completar la operación. Revisa los datos e inténtalo de nuevo.';
        this.requestError[1] = isDemo ? '' : 'Intentar de nuevo';
        this.formOperations = new FormOperations(this.form, this.model);
    }

    /**
     * Evaluates the form and sends the request when the payload is valid.
     */
    public submit(): void {
        this.validationError = null;
        this.validationSummary = null;

        this.refreshControlValidity();

        if (!this.model.valid && this.currentFormType !== FormType.DELETION) {
            this.displayErrorsOnButton();
            return;
        }

        if (this.recordHasAssociatedData && !this.warning) {
            this.warning = 'El registro tiene datos, por lo que se borrará toda la información asociada. <br> ¿Continuar?';
            return;
        }

        this.warning = null;
        this.hasSubmittedRequest = true;
        this.hasCompletedRequest = false;

        let payload = this.updateObjectWithIds(this.model.getRawValue());

        payload = this.form.payloadTransform?.(payload) ?? payload;

        if (this.currentFormType === FormType.DUPLICATION) {
            delete payload['id'];
        }

        this.sendRequest(payload as T);
    }

    /**
     * Replaces submitted dropdown objects with their primary key values.
     * @param data Raw form data.
     * @returns Serializable payload for the backend.
     */
    private updateObjectWithIds(data: Record<string, any>): Record<string, unknown> {
        const fieldMap = new Map<string, string>();

        for (const section of this.form.sections ?? []) {
            for (const field of section.fields ?? []) {
                const primaryKey = field.input.dropdownConfiguration?.valuePrimaryKey;

                if (primaryKey) {
                    fieldMap.set(field.name, primaryKey);
                }
            }
        }

        return Object.entries(data).reduce((result, [key, value]) => {
            const primaryKey = fieldMap.get(key) || 'id';

            if (Array.isArray(value)) {
                result[key] = value
                    .map((item) => item?.[primaryKey])
                    .filter(Boolean)
                    .join(';');
            } else if (value && typeof value === 'object' && value[primaryKey]) {
                result[key] = value[primaryKey];
            } else {
                result[key] = value;
            }

            return result;
        }, {} as Record<string, unknown>);
    }

    /**
     * Performs the configured request and updates the visual state only after a real submit.
     * @param payload Backend payload.
     */
    private sendRequest(payload: T): void {
        this.request.safePerform(payload);
        this.request.response((res) => {
            this.hasCompletedRequest = true;
            this.getRequest.safePerform();
            this.afterSend.emit(res);
            setTimeout(() => this.request.unsuscribe(), 1000);
            this.returnToMainView();
        });
    }

    /**
     * Returns the user to the previous location.
     */
    private returnToMainView(): void {
        this.goBack.emit();
    }

    private refreshControlValidity(): void {
        const controls = this.model.controls as Record<string, AbstractControl>;

        Object.values(controls).forEach((control) => control.updateValueAndValidity({ emitEvent: false }));
        this.model.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * Displays validation errors in an accessible summary near the submit button.
     */
    private displayErrorsOnButton(): void {
        const errorMessages: Record<string, (errors: Record<string, string>) => string> = {
            required: () => 'es obligatorio.',
            maxlength: () => 'ha excedido la longitud máxima.',
            email: () => 'no es un correo válido.',
            invalidPattern: (errors) => errors['invalidPattern'] ?? '',
            passwordRequired: (errors) => errors['passwordRequired'] ?? '',
            passwordRepeatRequired: (errors) => errors['passwordRepeatRequired'] ?? '',
            passwordMismatch: (errors) => errors['passwordMismatch'] ?? '',
            repeatableRowInvalid: (errors) => errors['repeatableRowInvalid'] ?? '',
            invalidTime: (errors) => errors['invalidTime'] ?? '',
            invalidSpanishId: (errors) => errors['invalidSpanishId'] ?? '',
            spanishIdTaken: (errors) => errors['spanishIdTaken'] ?? '',
        };

        const controls = this.model.controls as Record<string, AbstractControl>;
        const invalidFields = Object.entries(controls).reduce<string[]>((acc, [field, control]) => {
            if (!control?.invalid) {
                return acc;
            }

            const fieldInfo = this.formOperations.getFieldByControlName(field);
            const errors = control?.errors ?? {};

            const messages = Object.keys(errors)
                .filter((key) => key in errorMessages)
                .map((key) => errorMessages[key]?.(errors))
                .join(' ');

            const fieldLabel = String(fieldInfo?.label ?? field).trim() || field;

            acc.push(`${fieldLabel} ${messages || ': Error desconocido.'}`);
            return acc;
        }, []);

        this.validationError = 'Revisa los campos indicados.';
        this.validationSummary = `No hemos podido guardar todavía. Revisa estos campos:<br>- ${invalidFields.join('<br>- ')}`;
        this.model.markAllAsTouched();
    }
}
